import { OEP_ENTITY, OEP_FILE_CATEGORY, type OEPFileKind } from 'agu_oep_core';
import { DateOrNull, Str, StrOrNull, Up, type OEPRow } from './rows';

/** The latest Uploaded OEP file of a presentation (MJ: Files stores no size, so SizeBytes is usually null). */
export interface OEPFileInput { FileID: string; Name: string; ContentType: string | null; SizeBytes: number | null; UploadedAt: Date }

/** The newest Uploaded file of one OEP kind per presentation (upper-case Presentation ID → file), from MJ: Files rows (newest first) + their record links. */
export function OEPLatestFiles(files: readonly OEPRow[], links: readonly OEPRow[], kind: OEPFileKind): Map<string, OEPFileInput> {
    const recordOf = new Map(links.map((l) => [Up(Str(l, 'FileID')), Up(Str(l, 'RecordID'))]));
    const out = new Map<string, OEPFileInput>();
    for (const f of files) {
        if (Str(f, 'Category') !== OEP_FILE_CATEGORY[kind] || Str(f, 'Status') !== 'Uploaded') continue;
        const pid = recordOf.get(Up(Str(f, 'ID')));
        if (pid === undefined || out.has(pid)) continue;
        out.set(pid, { FileID: Str(f, 'ID'), Name: Str(f, 'Name'), ContentType: StrOrNull(f, 'ContentType'), SizeBytes: null, UploadedAt: DateOrNull(f, '__mj_CreatedAt') ?? new Date(0) });
    }
    return out;
}

/** The newest Uploaded draft per presentation. */
export function OEPLatestDrafts(files: readonly OEPRow[], links: readonly OEPRow[]): Map<string, OEPFileInput> {
    return OEPLatestFiles(files, links, 'Draft');
}

/** The link predicate for files of the given presentations (MJ: File Entity Record Links → Presentations). */
export function OEPPresentationLinkSql(presentationIDsSql: string): string {
    return `EntityID = (SELECT ID FROM __mj.Entity WHERE Name = '${OEP_ENTITY.Presentations}') AND TRY_CONVERT(UNIQUEIDENTIFIER, RecordID) IN (${presentationIDsSql})`;
}

/**
 * The MJ: Files predicate for Uploaded files of the given OEP kinds linked to the given presentations.
 * The Category filter is defense in depth: RLS (T5, tools/metadata/oep-permissions.js) already limits
 * each role to the categories it may read.
 */
export function OEPUploadedFilesSql(presentationIDsSql: string, kinds: readonly OEPFileKind[]): string {
    const categories = kinds.map((k) => `'${OEP_FILE_CATEGORY[k]}'`).join(',');
    return `Status='Uploaded' AND Category IN (${categories}) AND ID IN (SELECT FileID FROM __mj.FileEntityRecordLink WHERE ${OEPPresentationLinkSql(presentationIDsSql)})`;
}
